import {
  AbortMultipartDirectUploadOptions,
  GetEntireVideoInstanceByIdResult as ApiGetEntireVideoInstanceByIdResult,
  UploadVideoOptions as ApiUploadVideoOptions,
  CompleteMultipartDirectUploadOptions,
  CompleteMultipartDirectUploadResult,
  CreateEntireVideoInstanceArgs,
  CreateEntireVideoInstanceResult,
  EntireVideo,
  EntireVideoSearchOptions,
  GetEntireVideoByIdArgs,
  GetEntireVideoInstanceByIdArgs,
  GifConversionResult,
  InitMultipartDirectUploadResult,
  LayoutSegment,
  MediaContentInfo,
  MediaSegmentData,
  ParsedExternalMediaResult,
  PreSignMultipartDirectUploadPartsOptions,
  PreSignMultipartDirectUploadPartsResult,
  SignedUploadBody,
  TextBlob,
  VideoLayoutTimeline,
  VideoUpload,
  VideoUploadSearchOptions,
  VideoUploadSearchResult,
  VideoUploadTranscript,
} from '@sparemin/api-sdk';
import {
  IApiAction,
  IApiResponse,
  NormalizationListResult,
  NormalizationResult,
} from '../types';

type ActionKey = 'MEDIA_UPLOAD_SERVICE';
export const ACTION_KEY: ActionKey = 'MEDIA_UPLOAD_SERVICE';

export enum ServiceMethod {
  UPLOAD_TEXT = 'UPLOAD_TEXT',
  UPLOAD_VIDEO = 'UPLOAD_VIDEO',
  DIRECT_UPLOAD = 'DIRECT_UPLOAD',
  MULTIPART_DIRECT_UPLOAD_INIT = 'MULTIPART_DIRECT_UPLOAD_INIT',
  MULTIPART_DIRECT_UPLOAD_PRESIGN = 'MULTIPART_DIRECT_UPLOAD_PRESIGN',
  MULTIPART_DIRECT_UPLOAD_COMPLETE = 'MULTIPART_DIRECT_UPLOAD_COMPLETE',
  MULTIPART_DIRECT_UPLOAD_ABORT = 'MULTIPART_DIRECT_UPLOAD_ABORT',
  GET_VIDEO = 'GET_VIDEO',
  GET_VIDEOS = 'VIDEOS_GET',
  SEARCH_VIDEO = 'SEARCH_VIDEO',
  GET_TEXT = 'GET_TEXT',
  CONVERT_GIF_TO_VIDEO = 'CONVERT_GIF_TO_VIDEO',
  PARSE_EXTERNAL_CONTENT = 'PARSE_EXTERNAL_CONTENT',
  GET_MEDIA_CONTENT_INFO = 'GET_MEDIA_CONTENT_INFO',
  GET_MEDIA_SEGMENT_INFO_BY_REVISION_ID = 'GET_MEDIA_SEGMENT_INFO_BY_REVISION_ID',
  GET_VIDEO_LAYOUT_TIMELINE_BY_REVISION_ID = 'GET_VIDEO_LAYOUT_TIMELINE_BY_REVISION_ID',
  UPDATE_VIDEO_LAYOUT_TIMELINE_BY_REVISION_ID = 'UPDATE_VIDEO_LAYOUT_TIMELINE_BY_REVISION_ID',
  UPDATE_VIDEO_LAYOUT_TIMELINE_SEGMENT = 'UPDATE_VIDEO_LAYOUT_TIMELINE_SEGMENT',
  CREATE_ENTIRE_VIDEO_INSTANCE = 'CREATE_ENTIRE_VIDEO_INSTANCE',
  GET_ENTIRE_VIDEO_INSTANCE_BY_ID = 'GET_ENTIRE_VIDEO_INSTANCE_BY_ID',
  GET_ENTIRE_VIDEO_BY_ID = 'GET_ENTIRE_VIDEO_BY_ID',
  SEARCH_ENTIRE_VIDEO = 'SEARCH_ENTIRE_VIDEO',
}

export type Source = File | string;

export type UploadVideoOptions = Pick<
  ApiUploadVideoOptions,
  | 'directUploadBucket'
  | 'directUploadKey'
  | 'googleDriveFileId'
  | 'initiateProcessing'
  | 'language'
  | 'providerUserId'
  | 'youtubeUrl'
  | 'zoomMeetingId'
  | 'zoomRecordingFileId'
> & {
  trimStart?: number;
  trimEnd?: number;
  transcribe?: boolean;
};

export type UploadVideoToStorageOptions = Pick<
  ApiUploadVideoOptions,
  'trimStartMillis' | 'trimEndMillis' | 'language' | 'initiateProcessing'
> & {
  src: File | string;
};

export type IVideoUpload = VideoUpload;

export type NormalizedVideoUpload = VideoUpload & {
  transcript: VideoUploadTranscript & {
    id: number;
  };
};

export type VideoNormalizationResult = NormalizationResult<
  'videos',
  number,
  NormalizedVideoUpload
>;

export type VideoListNormalizationResult = NormalizationListResult<
  'videos',
  number,
  NormalizedVideoUpload
>;

export type TextNormalizationResult = NormalizationResult<
  'text',
  string,
  TextBlob
>;

export type UploadTextArgs = [string, string];
export type UploadTextAction = ApiAction<
  ServiceMethod.UPLOAD_TEXT,
  UploadTextArgs
>;

export type UploadVideoArgs = [
  Source, // file
  boolean, // transcribe
  number, // trimStart
  number, // trimEnd
  string, // lang
  string, // directUploadBucket
  string, // directUploadKey
  boolean, // initiateProcessing
  string, // providerUserId
  string, // googleDriveFileId
  string, // zoomMeetingId
  string, // zoomRecordingFileId
  string, // youtubeUrl
];
export type UploadVideoAction = ApiAction<
  ServiceMethod.UPLOAD_VIDEO,
  UploadVideoArgs
>;

export type SearchVideoArgs = VideoUploadSearchOptions;
export type SearchVideoAction = ApiAction<
  ServiceMethod.SEARCH_VIDEO,
  SearchVideoArgs
>;
export type SearchVideoResult = VideoUploadSearchResult;

export type GetVideoArgs = [number | string];
export type GetVideoAction = ApiAction<ServiceMethod.GET_VIDEO, GetVideoArgs>;

export type GetVideosOptions = {
  ids: number[];
  includeVariations?: boolean;
};
export type GetVideosArgs = [number[], boolean?];
export type GetVideosResult = VideoListNormalizationResult;
export type GetVideosAction = ApiAction<
  ServiceMethod.GET_VIDEOS,
  GetVideosArgs
>;

export type GetTextArgs = [string];
export type GetTextAction = ApiAction<ServiceMethod.GET_TEXT, GetTextArgs>;

export type ConvertGifToVideoArgs = [Source];
export type ConvertGifToVideoAction = ApiAction<
  ServiceMethod.CONVERT_GIF_TO_VIDEO,
  ConvertGifToVideoArgs
>;

export type ParseExternalContentArgs = [string /* url */];

export type ParseExternalContentAction = ApiAction<
  ServiceMethod.PARSE_EXTERNAL_CONTENT,
  ParseExternalContentArgs
>;

export type DirectUploadFileType = 'audio' | 'video';
export type DirectUploadArgs = [
  DirectUploadFileType,
  string, // content-type
  string, // filename
];

export type DirectUploadAction = ApiAction<
  ServiceMethod.DIRECT_UPLOAD,
  DirectUploadArgs
>;

export type InitMultipartDirectUploadAction = ApiAction<
  ServiceMethod.MULTIPART_DIRECT_UPLOAD_INIT,
  DirectUploadArgs
>;

export type PresignMultipartDirectUploadPartsAction = ApiAction<
  ServiceMethod.MULTIPART_DIRECT_UPLOAD_PRESIGN,
  PreSignMultipartDirectUploadPartsOptions
>;

export type CompleteMultipartDirectUploadAction = ApiAction<
  ServiceMethod.MULTIPART_DIRECT_UPLOAD_COMPLETE,
  CompleteMultipartDirectUploadOptions
>;

export type AbortMultipartDirectUploadAction = ApiAction<
  ServiceMethod.MULTIPART_DIRECT_UPLOAD_ABORT,
  AbortMultipartDirectUploadOptions
>;

export type GetMediaContentInfoArgs = [string /* url */];

export type GetMediaContentInfoAction = ApiAction<
  ServiceMethod.GET_MEDIA_CONTENT_INFO,
  GetMediaContentInfoArgs
>;

export type GetMediaSegmentDataByRevisionIdArgs = {
  uuid: string;
  revisionId: string;
};

type NormalizedMediaSegmentData = NormalizationResult<
  'mediaSegmentData',
  string,
  MediaSegmentData
>;

export type GetMediaSegmentDataByRevisionIdResult = NormalizedMediaSegmentData;

export type GetMediaSegmentDataByRevisionIdAction = ApiAction<
  ServiceMethod.GET_MEDIA_SEGMENT_INFO_BY_REVISION_ID,
  GetMediaSegmentDataByRevisionIdArgs
>;

export type NormalizedVideoLayoutTimeline = NormalizationResult<
  'videoLayoutTimeline',
  string,
  VideoLayoutTimeline
>;

export type GetVideoLayoutTimelineDataByRevisionIdArgs = {
  uuid: string;
  revisionId: string;
};

export type GetVideoLayoutTimelineDataByRevisionIdResult = NormalizedVideoLayoutTimeline;

export type GetVideoLayoutTimelineDataByRevisionIdAction = ApiAction<
  ServiceMethod.GET_VIDEO_LAYOUT_TIMELINE_BY_REVISION_ID,
  GetVideoLayoutTimelineDataByRevisionIdArgs
>;

export type UpdateVideoLayoutTimelineByRevisionIdArgs = VideoLayoutTimeline;

export type UpdateVideoLayoutTimelineByRevisionIdResult = NormalizedVideoLayoutTimeline;

export type UpdateVideoLayoutTimelineByRevisionIdAction = ApiAction<
  ServiceMethod.UPDATE_VIDEO_LAYOUT_TIMELINE_BY_REVISION_ID,
  UpdateVideoLayoutTimelineByRevisionIdArgs
>;

export type UpdateVideoLayoutTimelineSegmentArgs = {
  uuid: string;
  revisionId: string;
  segmentId: string;
  segment: Partial<LayoutSegment>;
  /*
   * this isn't strictly necessary, but it allows us to do an optimistic update
   * on the timeline.  The API doesn't return the udpated timeline, and the
   * entities reducer only does a shallow merge of the entity, so we'll lose
   * data if we just send a partial with the updated segment.
   */
  timeline: VideoLayoutTimeline;
};

export type UpdateVideoLayoutTimelineSegmentAction = ApiAction<
  ServiceMethod.UPDATE_VIDEO_LAYOUT_TIMELINE_SEGMENT,
  UpdateVideoLayoutTimelineSegmentArgs
>;

export type UpdateVideoLayoutTimelineSegmentResult = NormalizedVideoLayoutTimeline;

type ApiAction<M extends ServiceMethod, A> = IApiAction<ActionKey, M, A>;

export type CreateEntireVideoInstanceAction = ApiAction<
  ServiceMethod.CREATE_ENTIRE_VIDEO_INSTANCE,
  CreateEntireVideoInstanceArgs
>;

export type GetEntireVideoInstanceByIdAction = ApiAction<
  ServiceMethod.GET_ENTIRE_VIDEO_INSTANCE_BY_ID,
  GetEntireVideoInstanceByIdArgs
>;

export type GetEntireVideoInstanceByIdResult = NormalizationResult<
  'entireVideoInstances',
  number,
  ApiGetEntireVideoInstanceByIdResult
>;

export type GetEntireVideoByIdAction = ApiAction<
  ServiceMethod.GET_ENTIRE_VIDEO_BY_ID,
  GetEntireVideoByIdArgs
>;

export type GetEntireVideoByIdResult = NormalizationResult<
  'entireVideos',
  number,
  EntireVideo
>;

export type SearchEntireVideoArgs = EntireVideoSearchOptions;

export type SearchEntireVideoAction = ApiAction<
  ServiceMethod.SEARCH_ENTIRE_VIDEO,
  SearchEntireVideoArgs
>;

export type SearchEntireVideoResult = NormalizationListResult<
  'entireVideos',
  number,
  EntireVideo
>;

export interface MediaUploadServiceDispatch {
  (action: UploadTextAction | GetTextAction): Promise<
    IApiResponse<TextNormalizationResult>
  >;
  (action: ConvertGifToVideoAction): Promise<IApiResponse<GifConversionResult>>;
  (action: UploadVideoAction | GetVideoAction): Promise<
    IApiResponse<VideoNormalizationResult>
  >;
  (action: SearchVideoAction): Promise<IApiResponse<SearchVideoResult>>;
  (action: ParseExternalContentAction): Promise<
    IApiResponse<ParsedExternalMediaResult>
  >;
  (action: DirectUploadAction): Promise<IApiResponse<SignedUploadBody>>;
  (action: InitMultipartDirectUploadAction): Promise<
    IApiResponse<InitMultipartDirectUploadResult>
  >;
  (action: PresignMultipartDirectUploadPartsAction): Promise<
    IApiResponse<PreSignMultipartDirectUploadPartsResult>
  >;
  (action: CompleteMultipartDirectUploadAction): Promise<
    IApiResponse<CompleteMultipartDirectUploadResult>
  >;
  (action: AbortMultipartDirectUploadAction): Promise<IApiResponse<void>>;
  (action: GetMediaContentInfoAction): Promise<IApiResponse<MediaContentInfo>>;
  (action: GetMediaSegmentDataByRevisionIdAction): Promise<
    IApiResponse<GetMediaSegmentDataByRevisionIdResult>
  >;
  (action: GetVideosAction): Promise<IApiResponse<GetVideosResult>>;
  (action: GetVideoLayoutTimelineDataByRevisionIdAction): Promise<
    IApiResponse<GetVideoLayoutTimelineDataByRevisionIdResult>
  >;
  (action: UpdateVideoLayoutTimelineByRevisionIdAction): Promise<
    IApiResponse<UpdateVideoLayoutTimelineByRevisionIdResult>
  >;
  (action: UpdateVideoLayoutTimelineSegmentAction): Promise<
    IApiResponse<UpdateVideoLayoutTimelineSegmentResult>
  >;
  (action: CreateEntireVideoInstanceAction): Promise<
    IApiResponse<CreateEntireVideoInstanceResult>
  >;
  (action: GetEntireVideoInstanceByIdAction): Promise<
    IApiResponse<GetEntireVideoInstanceByIdResult>
  >;
  (action: GetEntireVideoByIdAction): Promise<
    IApiResponse<GetEntireVideoByIdResult>
  >;
  (action: SearchEntireVideoAction): Promise<
    IApiResponse<SearchEntireVideoResult>
  >;
}
